import { useQuery } from '@tanstack/react-query';
import { awaitJSON, LinearLambdaFetch, S3SignedUrlFetch } from '@src/utils/fetch-utils';
import { DnrRow, PlansData, SpotRow } from './linear-plans-constants';

// Loads a company's plans/spots for a week range. The lambda returns an S3 path whose
// (doubly JSON-encoded) object is the actual PlansData payload.
export const useLinearPlans = (
  company: string,
  start: string,
  end: string
): ReturnType<typeof useQuery<PlansData>> =>
  useQuery({
    queryKey: ['linearPlans', company, start, end],
    enabled: Boolean(company && start && end),
    queryFn: async (): Promise<PlansData> => {
      const plansRes = await LinearLambdaFetch('/plans_view', {
        params: { company, start, end }
      });
      const { path } = await awaitJSON<{ path: string }>(plansRes);
      const s3Res = await S3SignedUrlFetch(path);
      const raw = await awaitJSON<string>(s3Res);
      return JSON.parse(raw);
    }
  });

export interface NetworkSummaryParams {
  company: string;
  start: string;
  end: string;
  calendar: boolean;
}

export const fetchNetworkSummary = async (params: NetworkSummaryParams): Promise<string> => {
  const res = await LinearLambdaFetch('/network_summary', { params });
  const { csv } = await awaitJSON<{ csv: string }>(res);
  return csv;
};

export interface UpdateSpotsResponse {
  spotsWithCreditMemos: (string | number)[];
  nMemosCreated: number;
}

export const saveSpots = async (spots: SpotRow[]): Promise<UpdateSpotsResponse> => {
  const res = await LinearLambdaFetch('/update_spots', {
    method: 'POST',
    body: { data: spots }
  });
  return awaitJSON<UpdateSpotsResponse>(res);
};

export const deleteSpots = async (spots: SpotRow[]): Promise<void> => {
  await LinearLambdaFetch('/delete_spots', {
    method: 'POST',
    body: spots
  });
};

export const saveReconciliation = async (row: DnrRow & { status: string }): Promise<void> => {
  await LinearLambdaFetch('/linear_reconciliation', {
    method: 'POST',
    body: [row]
  });
};
